import type React from "react";

interface ContentLoaderProps {
  className?: string;
  message?: string;
}

/**
 * Content Loader Component
 * Displays skeleton shapes that mimic the actual content structure
 */
export const ContentLoader: React.FC<ContentLoaderProps> = ({
  className = "",
  message = "Loading content...",
}) => {
  return (
    <div
      className={`min-h-screen bg-gradient-to-br from-white to-gray-50 ${className}`}
    >
      <div className="animate-pulse">
        {/* Hero Section Skeleton */}
        <div className="h-[600px] bg-gray-200 relative">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center space-y-6 px-4 max-w-4xl">
              <div className="h-8 bg-gray-300 rounded w-64 mx-auto" />
              <div className="h-16 bg-gray-300 rounded w-full max-w-2xl mx-auto" />
              <div className="h-6 bg-gray-300 rounded w-96 mx-auto" />
              <div className="flex gap-4 justify-center mt-8">
                <div className="h-12 bg-gray-300 rounded w-40" />
                <div className="h-12 bg-gray-300 rounded w-40" />
              </div>
            </div>
          </div>
        </div>

        {/* Content Sections Skeleton */}
        <div className="container mx-auto px-4 py-16 space-y-16">
          {/* Cards Grid Section */}
          <div>
            <div className="h-10 bg-gray-200 rounded w-64 mb-8" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-gray-200 rounded-lg p-6 space-y-4">
                  <div className="h-48 bg-gray-300 rounded" />
                  <div className="h-6 bg-gray-300 rounded w-3/4" />
                  <div className="h-4 bg-gray-300 rounded w-full" />
                  <div className="h-4 bg-gray-300 rounded w-2/3" />
                </div>
              ))}
            </div>
          </div>

          {/* Gallery Section Skeleton */}
          <div>
            <div className="h-10 bg-gray-200 rounded w-48 mb-8" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <div key={i} className="aspect-square bg-gray-200 rounded-lg" />
              ))}
            </div>
          </div>

          {/* Call to Action Skeleton */}
          <div className="bg-gray-200 rounded-lg p-12 flex items-center justify-between">
            <div className="space-y-4 flex-1">
              <div className="h-8 bg-gray-300 rounded w-64" />
              <div className="h-4 bg-gray-300 rounded w-96" />
            </div>
            <div className="h-12 bg-gray-300 rounded w-40" />
          </div>
        </div>

        {/* Loading Message */}
        <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 bg-black/80 text-white px-6 py-3 rounded-full flex items-center space-x-3">
          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          <span className="text-sm font-medium">{message}</span>
        </div>
      </div>
    </div>
  );
};
